/*
  Reverse Transformation (Redux → Backend)

  This module handles the reverse transformation from the Redux document model
  to the exact format expected by backend saving endpoints like saveJourneyVersion.
  It strips null values and formats data according to backend requirements.
*/

import type {
  SaveJourneyVersionPayload,
  ApiNode,
  ApiEdge,
  JourneyDocument,
  ReconciliationConditions as ApiReconciliationConditions,
} from '../types';
import type { ReconciliationConditions as DocReconciliationConditions } from '../types/document';
import { mapDocNodeToSaveApiNode } from './transform/reverseNodeMapping';
import { mapDocEdgeToSaveApiEdge } from './transform/reverseEdgeMapping';

/**
 * Convert reconciliation conditions from document format (snake_case) to API format (camelCase)
 */
function convertReconciliationConditionsToApi(
  docConditions: DocReconciliationConditions,
): ApiReconciliationConditions {
  return docConditions.map(rule => ({
    id: rule.id,
    operator: rule.operator,
    conditions: rule.conditions.map(condition => ({
      id: condition.id,
      quantity: condition.quantity,
      branch: condition.branch,
      entityApplicationOutcome: condition.entity_application_outcome,
    })),
    journeyApplicationOutcome: rule.journey_application_outcome,
  }));
}

/**
 * Internal helper to transform nodes and edges from document model to API format
 *
 * This centralizes the transformation logic to avoid duplication across
 * reverseTransform and reverseTransformGraph functions.
 *
 * @param doc - The journey document containing nodes and edges
 * @returns Transformed nodes and edges ready for backend API
 */
function transformNodesAndEdges(doc: JourneyDocument): {
  nodes: ApiNode[];
  edges: ApiEdge[];
} {
  return {
    nodes: Object.values(doc.nodes).map(mapDocNodeToSaveApiNode),
    edges: Object.values(doc.edges).map(mapDocEdgeToSaveApiEdge),
  };
}

/**
 * Reverse transformation: Redux Document Model → Backend Save Payload
 *
 * This function orchestrates the complete transformation from the normalized
 * document model back to the backend API format for saving. It performs:
 * 1. Node conversion with null cleanup
 * 2. Edge conversion with null cleanup
 * 3. EOJ reconciliation conditions extraction
 * 4. Metadata and options merging
 *
 * @param doc - The journey document to save
 * @param options - Save options (active, changeLog, etc.)
 * @returns The payload ready for backend saving
 */
export function reverseTransform(
  doc: JourneyDocument,
  options: {
    active?: boolean;
    changeLog?: string;
    isMultiEntity?: boolean;
    journeyNotes?: string;
    parentVersion?: number;
    versionId?: number;
  } = {},
): SaveJourneyVersionPayload {
  // Transform nodes and edges specifically for backend saving
  const { nodes, edges } = transformNodesAndEdges(doc);

  // Build the complete payload matching what saveJourneyVersion expects
  const payload: SaveJourneyVersionPayload = {
    nodes,
    edges,
    start_node_id: doc.start_node_id,
    terminal_reconciliation_workflow_node_id: doc.terminal_reconciliation_workflow_node_id,

    // Include saveJourneyVersion parameters
    ...(options.active !== undefined && { active: options.active }),
    ...(options.changeLog !== undefined && { changeLog: options.changeLog }),
    ...(options.isMultiEntity !== undefined && { is_multi_entity: options.isMultiEntity }),
    ...(options.journeyNotes !== undefined && { notes: options.journeyNotes }),
    ...(options.parentVersion !== undefined && { parent_version: options.parentVersion }),
    ...(options.versionId !== undefined && { version_id: options.versionId }),
  };

  // Handle EOJ reconciliation conditions migration back to top-level
  // This matches what saveJourneyVersion expects for the 'decisions' parameter
  const terminalReconId = doc.terminal_reconciliation_workflow_node_id;
  if (terminalReconId && doc.nodes[terminalReconId]?.type === 'reconciliation_workflow') {
    const cfg = (doc.nodes[terminalReconId] as any).config || {};
    if (cfg.reconciliation_conditions && cfg.legacy) {
      // Convert from document format (snake_case) to API format (camelCase)
      payload.reconciliation_conditions = convertReconciliationConditionsToApi(
        cfg.reconciliation_conditions,
      );
    }
  }

  return payload;
}

/**
 * Convenience function to extract just the graph portion for backend compatibility
 *
 * This matches the return value of the existing toAPI({ graph }) function.
 * Useful when you only need the graph structure without save options.
 *
 * @param doc - The journey document
 * @returns The graph data in API format
 */
export function reverseTransformGraph(doc: JourneyDocument): {
  nodes: ApiNode[];
  edges: ApiEdge[];
  start_node_id: string;
} {
  // Transform nodes and edges specifically for backend saving
  const { nodes, edges } = transformNodesAndEdges(doc);

  return {
    nodes,
    edges,
    start_node_id: doc.start_node_id,
  };
}
